import { twoFactorClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";

// Create a lazy-loaded auth client
const createClient = (baseURL: string) =>
  createAuthClient({
    baseURL,
    plugins: [twoFactorClient()],
  });

type AuthClient = ReturnType<typeof createClient>;

let authClientInstance: AuthClient | null = null;

export function resolveAuthBaseUrl() {
  if (typeof window !== "undefined") {
    return window.location.origin;
  }

  return import.meta.env.VITE_BASE_URL || "http://localhost:5173";
}

function getAuthClient() {
  if (!authClientInstance) {
    const baseURL = resolveAuthBaseUrl();
    authClientInstance = createClient(baseURL);
  }
  return authClientInstance;
}

// Export a facade with all auth methods
export const auth = {
  // Use getters to ensure the client is initialized before use
  get signIn() {
    return getAuthClient().signIn;
  },
  get signUp() {
    return getAuthClient().signUp;
  },
  get signOut() {
    return getAuthClient().signOut;
  },
  get signInWithOAuth() {
    return getAuthClient().signIn.social;
  },
  get getSession() {
    return getAuthClient().getSession;
  },
  get session() {
    return getAuthClient().useSession;
  },
  get updateUser() {
    return getAuthClient().updateUser;
  },
  get deleteUser() {
    return getAuthClient().deleteUser;
  },
  get resetPassword() {
    return getAuthClient().resetPassword;
  },
  get changePassword() {
    return getAuthClient().changePassword;
  },
  get requestPasswordReset() {
    return getAuthClient().requestPasswordReset;
  },
  get sendVerificationEmail() {
    return getAuthClient().sendVerificationEmail;
  },
  get verifyEmail() {
    return getAuthClient().verifyEmail;
  },
  get twoFactor() {
    return getAuthClient().twoFactor;
  },
  get $client() {
    return getAuthClient();
  },
};
